import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import SignIn from "./components/SignIn";
import NotFound from "./components/NotFound";
import Header from "./components/Header";
import "./App.css";
import Dashboard from "./components/Dashboard";
import Help from "./components/Help";
import Error from "./components/Error";
import Profile from "./components/Profile";
import NoOpenMatterPage from "./components/NoOpenMatterPage";
import { useAuth } from "./context/AuthContext";
import { useTranslation } from "./context/TranslationContext";
import PaymentComplete from "./components/PaymentComplete";
import SupportPage from "./components/SupportPage";
import ContactUsPage from "./components/ContactUsPage";
import MultiMatters from "./components/MultiMatters";
import { MatterProvider, useMatter } from "./context/MatterContext";
import { ModulesProvider, useModules } from "./context/ModulesContext";
import ImportantConsumerInfoPage from "./components/ImportantConsumerInfoPage";
import SummaryUpdatesPage from "./components/SummaryUpdatesPage";
import ImportantIntroPage from "./components/ImportantIntroPage";
import ContactEditPage from "./components/ContactEditPage";
import FaqPage from "./components/FaqPage";
import Documents from "./components/Documents";
import CnpjPage from "./components/CnpjPage";
import Surveys from "./components/SurveyViewer";
import Dispute from "./components/Dispute";
import Mariana2026RetainerGate from "./components/Mariana2026RetainerGate";
import MarianaAnnouncementGate from "./components/MarianaAnnouncementGate";
import ProjectRRetainerGate from "./components/ProjectRRetainerGate";
import { shouldShowProjectRFlowForMatter } from "./modules/moduleUtils";

const ProtectedRoute = ({ element }) => {
  const { tokens, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return <p className="ml-20 mt-20">{t("loading")}</p>;
  }

  if (!tokens?.accessToken) {
    return <Navigate to="/signin" replace />;
  }

  return element;
};

const DisputeRoute = () => {
  const { party, matter, bootstrapping, bootstrap, bootstrapState } =
    useMatter();

  useEffect(() => {
    if (!party && !bootstrapping && bootstrapState !== "no_open_matter") {
      bootstrap();
    }
  }, [party, bootstrapping, bootstrapState, bootstrap]);

  if (bootstrapState === "no_open_matter") {
    return <NoOpenMatterPage />;
  }

  if (bootstrapping) {
    return <p className="ml-20 mt-20">Carregando...</p>;
  }

  if (!party || !matter) {
    return <Navigate to="/" replace />;
  }

  const { ff, pid, novel } = party?.compensation || {};

  const hasAccess = [ff, pid, novel].some((scheme) => {
    if (!scheme) return false;

    return (
      Boolean(scheme.agreementDate) ||
      Boolean(scheme.paymentDate) ||
      scheme.value != null
    );
  });

  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Dispute />;
};

const ProjectRRoute = ({ element }) => {
  const { party, matter, bootstrapping, bootstrap, bootstrapState } =
    useMatter();
  const { modules, modulesInitialized } = useModules();
  const params = useParams();

  useEffect(() => {
    if (!party && !bootstrapping && bootstrapState !== "no_open_matter") {
      bootstrap();
    }
  }, [party, bootstrapping, bootstrapState, bootstrap]);

  if (bootstrapState === "no_open_matter") {
    return <NoOpenMatterPage />;
  }

  if (bootstrapping || !modulesInitialized) {
    return <p className="ml-20 mt-20">Carregando...</p>;
  }

  if (!party || !matter) {
    return <Navigate to="/" replace />;
  }

  const routeMatterId = params?.matterId || "";
  const currentMatterId = routeMatterId || matter?.id || matter?.matterId || "";

  if (!shouldShowProjectRFlowForMatter(modules, currentMatterId)) {
    return <Navigate to="/dashboard" replace />;
  }

  return element;
};

function Layout({ children }) {
  const location = useLocation();
  const hideHeaderOn = ["/signin", "/bank"];
  const shouldHideHeader = hideHeaderOn.includes(location.pathname);

  return (
    <>
      {!shouldHideHeader && <Header />}
      {children}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = "PG-BHPClientPortal";
  }, []);

  return (
    <MatterProvider>
      <ModulesProvider>
        <Router>
          <Layout>
            <Mariana2026RetainerGate>
              <ProjectRRetainerGate>
                <MarianaAnnouncementGate>
                  <Routes>
                    <Route
                      path="/"
                      element={<ProtectedRoute element={<MultiMatters />} />}
                    />
                    <Route
                      path="/multimatters"
                      element={<ProtectedRoute element={<MultiMatters />} />}
                    />
                    <Route
                      path="/dashboard"
                      element={<ProtectedRoute element={<Dashboard />} />}
                    />
                    <Route
                      path="/contact-us"
                      element={<ProtectedRoute element={<ContactUsPage />} />}
                    />
                    <Route
                      path="/return"
                      element={<ProtectedRoute element={<PaymentComplete />} />}
                    />
                    <Route
                      path="/profile"
                      element={<ProtectedRoute element={<Profile />} />}
                    />
                    <Route
                      path="/contact-edit"
                      element={<ProtectedRoute element={<ContactEditPage />} />}
                    />
                    <Route
                      path="/faq"
                      element={<ProtectedRoute element={<FaqPage />} />}
                    />
                    <Route
                      path="/documents"
                      element={<ProtectedRoute element={<Documents />} />}
                    />
                    <Route
                      path="/important-consumer-info"
                      element={
                        <ProtectedRoute
                          element={
                            <ProjectRRoute
                              element={<ImportantConsumerInfoPage />}
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/summary-updates"
                      element={
                        <ProtectedRoute
                          element={
                            <ProjectRRoute element={<SummaryUpdatesPage />} />
                          }
                        />
                      }
                    />
                    <Route
                      path="/dispute"
                      element={<ProtectedRoute element={<DisputeRoute />} />}
                    />
                    <Route
                      path="/surveys"
                      element={<ProtectedRoute element={<Surveys />} />}
                    />

                    <Route
                      path="/clients/:partyId/matters/:matterId/documents"
                      element={<ProtectedRoute element={<Documents />} />}
                    />
                    <Route
                      path="/clients/:partyId/matters/:matterId/important-consumer-info"
                      element={
                        <ProtectedRoute
                          element={
                            <ProjectRRoute
                              element={<ImportantConsumerInfoPage />}
                            />
                          }
                        />
                      }
                    />
                    <Route
                      path="/clients/:partyId/matters/:matterId/summary-updates"
                      element={
                        <ProtectedRoute
                          element={
                            <ProjectRRoute element={<SummaryUpdatesPage />} />
                          }
                        />
                      }
                    />

                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/cnpj" element={<CnpjPage />} />
                    <Route path="/help" element={<Help />} />
                    <Route
                      path="/important"
                      element={
                        <ProtectedRoute
                          element={
                            <ProjectRRoute element={<ImportantIntroPage />} />
                          }
                        />
                      }
                    />
                    <Route
                      path="/news"
                      element={<Navigate to="/dashboard" replace />}
                    />
                    <Route path="/error" element={<Error />} />
                    <Route path="/support" element={<SupportPage />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </MarianaAnnouncementGate>
              </ProjectRRetainerGate>
            </Mariana2026RetainerGate>
          </Layout>
        </Router>
      </ModulesProvider>
    </MatterProvider>
  );
}

export default App;
